import {
  type Locale,
  RainbowKitProvider,
  type Theme,
  darkTheme,
  useConnectModal,
} from '@rainbow-me/rainbowkit';
import { theme } from '@venusprotocol/ui';
import { reconnect as wagmiReconnect } from '@wagmi/core';
import { merge } from 'lodash-es';
import { type PropsWithChildren, useEffect, useRef } from 'react';
import { useAccount, useConfig } from 'wagmi';

import '@rainbow-me/rainbowkit/styles.css';
import { useTranslation } from 'libs/translations';
import { useSyncWalletChainOnConnect } from 'libs/wallet/hooks/useSyncWalletChainOnConnect';

export interface RainwbowKitWrapperProps extends PropsWithChildren {}

const rkTheme = merge(
  darkTheme({
    accentColor: theme.colors.blue,
    borderRadius: 'small',
  }),
  {
    fonts: {
      body: '"ProximaNova", var(--font-fallback)',
    },
    colors: {
      closeButtonBackground: 'transparent',
      generalBorder: theme.colors['dark-blue-hover'],
      selectedOptionBorder: theme.colors['dark-blue-hover'],
      modalBorder: theme.colors.blue,
      modalBackground: theme.colors['dark-blue'],
    },
  } as Theme,
);

// Logs the RAW chain state reported by wagmi/RainbowKit (before any of our own resolution logic),
// so we can tell whether the wallet/WC connected on the wrong chain (wagmi/RainbowKit/usage issue)
// or whether it connected correctly but our dApp derives the wrong chain (our bug).
const ChainDebugLogger: React.FC = () => {
  const { address, chainId, chain, status, connector } = useAccount();

  useEffect(() => {
    console.log(
      `[CHAIN_DEBUG] wagmi:useAccount(raw) | status=${status} | walletChainId=${chainId} | chainName=${
        chain?.name ?? 'unknown'
      } | connector=${connector?.name ?? 'none'} | connectorId=${connector?.id ?? 'none'} | address=${
        address ?? 'none'
      }`,
    );
  }, [address, chainId, chain?.name, status, connector?.name, connector?.id]);

  return null;
};

const WalletChainSync: React.FC = () => {
  useSyncWalletChainOnConnect();
  return null;
};

const ConnectionRecovery: React.FC = () => {
  const config = useConfig();
  const { connectModalOpen } = useConnectModal();
  const { status } = useAccount();
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!connectModalOpen || status !== 'connecting') {
      clearTimeout(timerRef.current);
      return;
    }

    timerRef.current = setTimeout(async () => {
      await wagmiReconnect(config);
    }, 5000);

    return () => clearTimeout(timerRef.current);
  }, [connectModalOpen, status, config]);

  return null;
};

export const RainwbowKitWrapper: React.FC<RainwbowKitWrapperProps> = ({ children }) => {
  const { language } = useTranslation();

  return (
    <RainbowKitProvider
      locale={language.bcp47Tag as Locale}
      appInfo={{
        appName: 'Venus',
      }}
      theme={rkTheme}
    >
      <ChainDebugLogger />
      <WalletChainSync />
      <ConnectionRecovery />
      {children}
    </RainbowKitProvider>
  );
};
